import moment from 'moment-timezone';
import { campaignTypeMap } from '../../../../core/utils/constants/constants';
import { calculateImpressions } from '../CampaignRequestorUtil';
import { getPricingValueForDisplay } from '../../../../core/utils/pricing/pricingFieldMapper';
import {
  toStartOfDayUtc,
  toEndOfDayUtc,
  getDefaultStartDate,
  getDefaultEndDate,
} from './dateNormalization';

/**
 * Extracted form initialization logic for the new hook-based approach
 * This replaces the handleInitialValues function from the original component
 */
export const handleInitialValues = (campaignOrder, form, history, selectedAdvertiser, timezone = null) => {
  const {
    name,
    description,
    startDate,
    endDate,
    type,
    renewalStatus,
    advertiser,
    budget,
    cpm,
    isRegulated,
    category,
    attributionTargets,
    geoTargets,
    measureWalkInTraffic,
    advertiserType,
    transactionTarget,
    transactionsConfig,
    signUpConfig,
    awarenessCampaign,
    sessionConfig,
    notes,
    campaigns,
    freeImpressions,
    IONumber,
    additionalIONumbers,
    productCode,
  } = campaignOrder;

  // Process line item data for a single line item
  const processLineItem = (lineItem, campaignBudget, selectedAdvertiser) => {
    // Parse dates through the normalized TZ → UTC pipeline
    const processedStartDate = toStartOfDayUtc(lineItem.startDate, timezone);
    const processedEndDate = toEndOfDayUtc(lineItem.endDate, timezone);

    // Safe budget calculation
    const safeBudgetTotal = Number(lineItem.budgetTotal) || 0;
    const safeCpm = Number(lineItem.cpm) || 7;
    const safeBudget = Number(campaignBudget) || 0;

    // Calculate impressions
    const impressions = calculateImpressions({
      budget: safeBudgetTotal,
      cpm: safeCpm,
    });

    // Calculate budget percentage
    const budgetPercentage = safeBudgetTotal && safeBudget
      ? Math.round((safeBudgetTotal / safeBudget) * 100)
      : 100;

    // Process targeting countries safely
    const targetingCountries = lineItem.targetingCountries &&
      Array.isArray(lineItem.targetingCountries) &&
      lineItem.targetingCountries.length > 0 &&
      lineItem.targetingCountries[0] != null
      ? lineItem.targetingCountries[0].code
      : null;

    // Enhanced ad group processing for cloned data
    let processedAdGroup = null;
    if (lineItem.adGroup) {
      processedAdGroup = {
        ...lineItem.adGroup,
        adGroupCreatives: (lineItem.adGroup.adGroupCreatives && Array.isArray(lineItem.adGroup.adGroupCreatives))
          ? lineItem.adGroup.adGroupCreatives
            .filter((creative) => creative != null)
            .map((creative) => {
              return {
                ...creative,
                startDate: toStartOfDayUtc(creative.startDate, timezone),
                endDate: toEndOfDayUtc(creative.endDate, timezone),
                // Ensure media files are preserved
                medias: creative.medias && Array.isArray(creative.medias) ? creative.medias : [],
              };
            })
          : [],
      };
    }

    return {
      ...lineItem,
      startDate: processedStartDate,
      endDate: processedEndDate,
      impressions,
      budgetPercentage,
      targetingCountries,
      adGroup: processedAdGroup,
      // Ensure targeting data is preserved
      targetingRegions: lineItem.targetingRegions && Array.isArray(lineItem.targetingRegions) ? lineItem.targetingRegions : [],
      targetingLocations: lineItem.targetingLocations && Array.isArray(lineItem.targetingLocations) ? lineItem.targetingLocations : [],
      firstPartyDataAudiences: lineItem.firstPartyDataAudiences && Array.isArray(lineItem.firstPartyDataAudiences) ? lineItem.firstPartyDataAudiences : [],
      dealIds: lineItem.dealIds && Array.isArray(lineItem.dealIds) ? lineItem.dealIds : [],
      dspDealIds: lineItem.dspDealIds && Array.isArray(lineItem.dspDealIds)
        ? lineItem.dspDealIds.map((deal) => ({
            id: deal.dspInternalId || deal.id,  // Preserve internal Nexxen ID for API
            dealId: deal.dealId,
            name: deal.name,
            cpmBid: deal.cpmBid || null,
          }))
        : [],
      // Map segments from GraphQL (segments) to form state (nexxenSegments)
      nexxenSegments: lineItem.segments && Array.isArray(lineItem.segments)
        ? lineItem.segments.map((segment) => ({
            segmentId: segment.segmentId,
            name: segment.name,
            segmentType: segment.segmentType,
            cost: segment.cost || 0,
            id: segment.id, // Preserve ID for update operations
          }))
        : [],
      // Normalize flight schedules: API returns flightSchedules (plural),
      // but the form and save manager use flightSchedule (singular).
      flightSchedule: (() => {
        const rawFlights = lineItem.flightSchedules || lineItem.flightSchedule || [];
        if (!Array.isArray(rawFlights) || rawFlights.length === 0) return [];
        return rawFlights.map((flight) => ({
          ...flight,
          id: flight.id || undefined,
          startDate: toStartOfDayUtc(flight.startDate, timezone),
          endDate: toEndOfDayUtc(flight.endDate, timezone),
          budget: Number(flight.budget) || 0,
          cpm: Number(flight.cpm) || 7,
        }));
      })(),
      // Explicitly preserve radio button selections for cloning
      targetingType: lineItem.targetingType || null,
      audienceType: lineItem.audienceType || null,
      // SSP & BID SETTINGS defaults
      supplySidePlatform: lineItem.supplySidePlatform || 'Smart AdServer',
      maxBid: lineItem.maxBid ?? 4,
      frequencyCap: lineItem.frequencyCap ?? 4,
    };
  };

  // Map campaigns array to form structure
  const formCampaigns = campaigns && Array.isArray(campaigns) && campaigns.length > 0
    ? campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.name || '',
        type: campaignTypeMap[campaign.type] || campaignTypeMap[type] || 1, // Use campaign-specific type, fallback to order type
        budget: Number(campaign.budgetTotal) || 0,
        // Parse dates through the normalized TZ → UTC pipeline
        startDate: toStartOfDayUtc(campaign.startDate, timezone),
        endDate: toEndOfDayUtc(campaign.endDate, timezone),
        cpm: Number(campaign.cpm) || Number(getPricingValueForDisplay(campaign, selectedAdvertiser)) || 7,
        advertiserPercentage: campaign.advertiserPercentage ? Number(campaign.advertiserPercentage) : null,
        contractPercentage: campaign.contractPercentage ? Number(campaign.contractPercentage) : null,
        hasMediaPlanStrategy: campaign.hasMediaPlanStrategy ?? false,
        valueAdd: campaign.valueAdd ?? false,
        demandSidePlatform: campaign.demandSidePlatform || null,
        adProviderId: campaign.adProviderId || null,
        maxBid: campaign.maxBid ? Number(campaign.maxBid) : null,
        // Campaign-level flight schedules (maps to Nexxen IO budget.schedules[])
        // Store as both plural (API naming) and singular (form/save-manager naming)
        flightSchedules: campaign.flightSchedules && Array.isArray(campaign.flightSchedules)
          ? campaign.flightSchedules.map((flight) => ({
              ...flight,
              startDate: toStartOfDayUtc(flight.startDate, timezone),
              endDate: toEndOfDayUtc(flight.endDate, timezone),
              budget: Number(flight.budget) || 0,
              cpm: Number(flight.cpm) || 7,
            }))
          : [],
        flightSchedule: campaign.flightSchedules && Array.isArray(campaign.flightSchedules)
          ? campaign.flightSchedules.map((flight) => ({
              ...flight,
              startDate: toStartOfDayUtc(flight.startDate, timezone),
              endDate: toEndOfDayUtc(flight.endDate, timezone),
              budget: Number(flight.budget) || 0,
              cpm: Number(flight.cpm) || 7,
            }))
          : [],
        isLaunchProgrammatically: campaign.isLaunchProgrammatically ?? false,
        status: campaign.status || null,
        lineItems: campaign.lineItems && Array.isArray(campaign.lineItems)
          ? campaign.lineItems.map(lineItem => processLineItem(lineItem, campaign.budgetTotal, selectedAdvertiser))
          : [],
      }))
    : [
        // Default campaign structure
        {
          name: '',
          type: campaignTypeMap[type] || 1,
          budget: budget || 0,
          startDate: null,
          endDate: null,
          hasMediaPlanStrategy: false,
          valueAdd: false,
          demandSidePlatform: null,
          adProviderId: null,
          maxBid: null,
          isLaunchProgrammatically: false,
          lineItems: [
            {
              name: 'Line Item 1',
              startDate: getDefaultStartDate(timezone),
              endDate: getDefaultEndDate(timezone),
              budgetTotal: budget || 0,
              budgetPercentage: 100,
              cpm: 7,
              regionGroups: [],
              targetingGroups: [],
              targetingType: null,
              targetingRegions: [],
              targetingLocations: [],
              targetingCountries: null,
              audienceType: null,
              firstPartyDataAudiences: [],
              adGroup: null,
              valueAdd: false,
              markedForDelivery: true, // Line items are marked for delivery by default
              // SSP & BID SETTINGS defaults
              supplySidePlatform: 'Smart AdServer',
              maxBid: 4,
              frequencyCap: 4,
            },
          ],
        },
      ];

  const values = {
    name: name || '',
    description,
    // Parse dates through the normalized TZ → UTC pipeline
    startDate: toStartOfDayUtc(startDate, timezone) || getDefaultStartDate(timezone),
    endDate: toEndOfDayUtc(endDate, timezone) || getDefaultEndDate(timezone),
    type: campaignTypeMap[type],
    renewalStatus: renewalStatus || 'EXPECTED_TO_RENEW',
    advertiserId: advertiser ? advertiser.id : null,
    budget: budget || 0,
    cpm: cpm || 7,
    freeImpressions: freeImpressions || 0,
    isRegulated,
    category,
    attributionTargets: attributionTargets || [],
    geoTargets: geoTargets || [],
    measureWalkInTraffic,
    advertiserType: advertiserType || 'LOCAL_DELIVERY_SERVICE',
    transactionTarget,
    transactionsConfig: transactionsConfig || null,
    signUpConfig: signUpConfig || null,
    awarenessCampaign,
    sessionConfig: sessionConfig || null,
    notes: notes || null,
    IONumber: IONumber || '',
    additionalIONumbers: additionalIONumbers || [],
    productCode:
      productCode ||
      (() => {
        // Set productCode based on campaign type if not already set
        const campaignType = campaignTypeMap[type];
        if (campaignType === 7) return 'EXT'; // External
        if (campaignType === 8) return 'CTV'; // Video
        return 'DIS'; // Display (default)
      })(),
    complianceAudiences: campaignOrder.complianceAudiences || [],
    campaigns: formCampaigns,
    impressions: calculateImpressions({ budget, cpm }),
  };

  form.setFieldsValue(values);

  // Force form re-render to ensure UI updates - critical for cloning
  setTimeout(() => {
    // Trigger a form validation to force re-render
    form.validateFields().catch(() => {
      // Ignore validation errors, we just want to trigger re-render
    });

    // Double-check that campaigns were set
    const currentFormValues = form.getFieldsValue();
  }, 100);

  // Only navigate if we have a real campaign ID (not during cloning)
  if (campaignOrder.id && history) {
    history.push(`/campaigns/requester/edit/${campaignOrder.id}`);
  } else if (!campaignOrder.id) {
    // For cloning operations, ensure the form values stick by triggering an additional refresh
    setTimeout(() => {
      form.setFieldsValue(values);
      form.validateFields().catch(() => {});
    }, 200);
  }

  return values;
};